import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, FileText, ShieldCheck, CheckCircle2, Lock, Pencil, Download, Info, Mail } from 'lucide-react'
import { MailExportDialog } from '@/components/mail/MailExportDialog'
import { useAuthStore } from '@/lib/auth.store'
import { Modal } from '@symphenia/ui-components'
import { fmtDate } from '@/lib/format-date'
import { downloadFile } from '@/lib/download'
import { apiErrorText } from '@/lib/api-error'
import {
  monthlyReportApi,
  type MonthlyReport,
  type MonthlyReportColumn,
  type MonthlyReportHoursFormat,
  type MonthlyReportRow,
} from '../api/monthly-report.api'
import { ReportDayDialog } from '../components/ReportDayDialog'
import { pageWide } from '@/lib/form-styles'

const PIN_MIN = 4

function formatHours(min: number, format: MonthlyReportHoursFormat): string {
  if (format === 'hhmm') {
    const h = Math.floor(min / 60)
    const m = min % 60
    return `${h}:${String(m).padStart(2, '0')}`
  }
  // Dezimal: max. 2 Kommastellen, keine überflüssigen Nullen (8 · 8,5 · 8,25)
  const rounded = Math.round((min / 60) * 100) / 100
  return rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '').replace('.', ',')
}

/**
 * AP-24: Ja/Nein-Feld (Typ B, Checkbox). Die Werte liegen als Boolean ODER als
 * String in der Datenbank — je nachdem, welche Maske sie geschrieben hat.
 * Ein Feld ohne Wert bleibt „–": nicht erfasst ist nicht dasselbe wie Nein.
 */
function formatBoolean(v: unknown): string {
  if (v === true || v === 'true') return 'Ja'
  if (v === false || v === 'false') return 'Nein'
  return '–'
}

const WEEKDAYS_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']

/** Alle Kalendertage eines Monats als YYYY-MM-DD. */
function daysOfMonth(year: number, month: number): string[] {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const mm = String(month).padStart(2, '0')
  return Array.from({ length: last }, (_, i) => `${year}-${mm}-${String(i + 1).padStart(2, '0')}`)
}

function weekdayOf(date: string): string {
  return WEEKDAYS_DE[new Date(`${date}T00:00:00.000Z`).getUTCDay()]
}

/** Montag = Beginn einer neuen Woche (Trennlinie in der Tabelle). */
function isWeekStart(date: string): boolean {
  return new Date(`${date}T00:00:00.000Z`).getUTCDay() === 1
}

/** Samstag und Sonntag werden gedämpft dargestellt. */
function isWeekend(date: string): boolean {
  const d = new Date(`${date}T00:00:00.000Z`).getUTCDay()
  return d === 0 || d === 6
}

/** Leerzeile für einen Tag ohne jede Erfassung (nur in der Alle-Tage-Ansicht). */
function emptyRow(date: string): MonthlyReportRow {
  return {
    date,
    weekday: weekdayOf(date),
    grossMinutes: 0,
    breakMinutes: 0,
    netMinutes: 0,
    absenceMinutes: 0,
    absenceDescription: '',
    totalWithAbsenceMinutes: 0,
    lv: {},
    source: 'timeEntries',
    // AP-26: leer heißt hier „nichts erfasst" — leerer String bzw. 0.
    startTime: '',
    endTime: '',
    departure: '',
    returnTime: '',
    travelMinutes: 0,
    km: 0,
    isDriver: false,
    overnight: false,
    appointment: '',
    appointmentMinutes: 0,
  }
}

/** Rohen Backend-Fehler-Key auslesen (für Steuerungslogik, NICHT übersetzt). */
function errorKey(err: unknown): string | undefined {
  return (err as { response?: { data?: { message?: string } } })?.response?.data?.message
}

export default function MonthlyReportPage() {
  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth() + 1)

  const [report, setReport] = useState<MonthlyReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // ── AP-24: händische Erfassung ────────────────────────────────────────────
  const [editDate, setEditDate] = useState<string | null>(null)
  const [prefilling, setPrefilling] = useState(false)
  const [hint, setHint] = useState<string | null>(null)

  // Abgabe-Dialog
  const [showSubmit, setShowSubmit] = useState(false)
  const [authMode, setAuthMode] = useState<'password' | 'pin'>('password')
  const [pin, setPin] = useState('')
  const [password, setPassword] = useState('')
  const [accepted, setAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [justSubmitted, setJustSubmitted] = useState(false)
  // AP-10 Nachtrag 5: per E-Mail — für Kunden, die nicht in Symphenia weiterverarbeiten.
  const [mailOffen, setMailOffen] = useState(false)
  const { t } = useTranslation()
  const ich = useAuthStore((s) => s.user)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const { data } = await monthlyReportApi.get(year, month)
      setReport(data)
    } catch (err) {
      setLoadError(apiErrorText(err, 'Bericht konnte nicht geladen werden.'))
      setReport(null)
    } finally {
      setLoading(false)
    }
  }, [year, month])

  useEffect(() => {
    void load()
  }, [load])

  const shiftMonth = (dir: -1 | 1) => {
    setJustSubmitted(false)
    let m = month + dir
    let y = year
    if (m < 1) { m = 12; y -= 1 }
    if (m > 12) { m = 1; y += 1 }
    setMonth(m)
    setYear(y)
  }

  const viewPdf = async () => {
    try {
      const res = await monthlyReportApi.exportPdf(year, month)
      const filename = `Monatsbericht_${year}-${String(month).padStart(2, '0')}.pdf`
      downloadFile(res.data as ArrayBuffer, filename, 'application/pdf')
    } catch (err) {
      setLoadError(apiErrorText(err, 'PDF konnte nicht erzeugt werden.'))
    }
  }

  const viewSubmittedPdf = async () => {
    if (!report?.submission) return
    try {
      const res = await monthlyReportApi.submissionPdf(report.submission.id)
      const filename = `Monatsbericht_${year}-${String(month).padStart(2, '0')}.pdf`
      downloadFile(res.data as ArrayBuffer, filename, 'application/pdf')
    } catch (err) {
      setLoadError(apiErrorText(err, 'Abgegebenes PDF konnte nicht geladen werden.'))
    }
  }

  const openSubmit = () => {
    setAuthMode('password')
    setPin('')
    setPassword('')
    setAccepted(false)
    setSubmitError(null)
    setShowSubmit(true)
  }

  const credentialReady = authMode === 'pin' ? pin.length >= PIN_MIN : password.length > 0

  const submit = async () => {
    if (!accepted || !credentialReady) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await monthlyReportApi.submit({
        year,
        month,
        confirmationAccepted: accepted,
        ...(authMode === 'pin' ? { pin } : { password }),
      })
      setShowSubmit(false)
      setJustSubmitted(true)
      await load()
    } catch (err) {
      setSubmitError(apiErrorText(err, 'Abgabe fehlgeschlagen. Bitte erneut versuchen.'))
      // Kein PIN hinterlegt → automatisch auf Passwort umschalten.
      if (errorKey(err) === 'monthlyReport.pinNotSet') setAuthMode('password')
      setPin('')
      setPassword('')
    } finally {
      setSubmitting(false)
    }
  }

  const cellValue = (col: MonthlyReportColumn, row: MonthlyReportRow): string => {
    if (col.key === 'date') return fmtDate(row.date)
    if (col.key === 'weekday') return row.weekday
    if (col.key === 'absenceDescription') return row.absenceDescription || '–'
    if (col.isLv) {
      const v = row.lv[col.key.slice(3)]
      if (col.kind === 'boolean') return formatBoolean(v)
      return v != null && v !== '' ? String(v) : '–'
    }
    // AP-26: Uhrzeiten und Freitext kommen fertig vom Server — hier wird nicht
    // noch einmal formatiert (die Wall-Clock-Umrechnung ist dort passiert).
    if (col.kind === 'time' || col.kind === 'text') {
      return (row as unknown as Record<string, string>)[col.key] || '–'
    }
    if (col.kind === 'number') {
      const n = (row as unknown as Record<string, number>)[col.key] ?? 0
      return n !== 0 ? String(n) : '–'
    }
    // AP-26: getypte Ja/Nein-Spalte (Lenker, Nächtigung). Anders als bei den
    // Diäten ist `false` hier ein erfasster Wert und heißt „Nein".
    if (col.kind === 'boolean') {
      return (row as unknown as Record<string, boolean>)[col.key] ? 'Ja' : 'Nein'
    }
    const min = (row as unknown as Record<string, number>)[col.key] ?? 0
    return min > 0 ? formatHours(min, report!.hoursFormat) : '–'
  }

  const totalValue = (col: MonthlyReportColumn): string => {
    if (!report || !col.summable) return ''
    if (col.isLv) {
      const t = report.totals.lv[col.key.slice(3)]
      return t != null ? String(t) : ''
    }
    // AP-26: Kilometer als Zahl, Lenker/Nächtigung als Anzahl der Ja-Tage.
    if (col.kind === 'number' || col.kind === 'boolean') {
      return String((report.totals as unknown as Record<string, number>)[col.key] ?? 0)
    }
    const min = (report.totals as unknown as Record<string, number>)[col.key] ?? 0
    return formatHours(min, report.hoursFormat)
  }

  const alignCls = (a: string) =>
    a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left'

  const locked = report?.locked ?? false
  const hasRows = (report?.rows.length ?? 0) > 0

  // ── AP-24: Bearbeitung nur im händischen Modus und nur im offenen Monat ────
  const canEdit = report?.timeSource === 'MANUAL' && !locked

  // Sobald bearbeitet werden darf, steht IMMER der ganze Monat da: ein Tag ohne
  // Eintrag ist genau der, den man anklicken will.
  const allDays = canEdit

  // AP-24: Im Zeiterfassungs-Modus ist der Bericht ein Bericht — die Stunden
  // kommen aus den Stempelungen, Fehlzeiten und Diäten aus ihren eigenen Masken.
  // Ohne diesen Hinweis steht man vor einer Liste, die sich grundlos nicht
  // anfassen lässt. Der gesperrte Monat hat schon sein eigenes Banner.
  const nurAnzeige = report?.timeSource === 'TIME_ENTRIES' && !locked
  const hatDiaeten = report?.columns.some((c) => c.isLv) ?? false

  const displayRows: MonthlyReportRow[] = (() => {
    if (!report) return []
    if (!allDays) return report.rows
    const byDate = new Map(report.rows.map((r) => [r.date, r]))
    return daysOfMonth(report.period.year, report.period.month).map(
      (d) => byDate.get(d) ?? emptyRow(d),
    )
  })()

  const runPrefill = async () => {
    if (!report) return
    setPrefilling(true)
    setLoadError(null)
    setHint(null)
    try {
      const { data } = await monthlyReportApi.prefill(year, month)
      const { filled, skipped } = data.data
      setHint(
        filled === 0
          ? 'Nichts zu übernehmen — alle Tage mit Stempelung sind bereits erfasst.'
          : `${filled} ${filled === 1 ? 'Tag' : 'Tage'} übernommen${skipped > 0 ? `, ${skipped} bereits erfasst und unverändert gelassen` : ''}.`,
      )
      await load()
    } catch (err) {
      setLoadError(apiErrorText(err, 'Vorbefüllen fehlgeschlagen.'))
    } finally {
      setPrefilling(false)
    }
  }

  return (
    // Volle Seitenbreite: Der Bericht führt bis zu 18 Spalten. Mit `max-w-5xl`
    // scrollte die Tabelle waagrecht, während links und rechts Rand frei blieb.
    <div className={pageWide}>
      {/* Kopf + Monatsnavigation */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-primary">Mein Monatsbericht</h1>
          <p className="text-sm text-muted-foreground">
            Prüfe deine Zeiten und gib den Monat rechtsverbindlich ab.
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => shiftMonth(-1)}
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
            aria-label="Vorheriger Monat"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="min-w-[9.5rem] text-center text-base font-medium text-foreground">
            {report?.period.label ?? '…'}
          </span>
          <button
            onClick={() => shiftMonth(1)}
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
            aria-label="Nächster Monat"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Statuszeile */}
      {locked && report?.submission && (
        <div className="flex items-center gap-2 rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2 text-sm text-green-700 dark:text-green-400">
          <Lock size={16} className="shrink-0" />
          <span>
            Verbindlich abgegeben am{' '}
            {new Date(report.submission.submittedAt).toLocaleString(undefined, {
              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
            })}
            . Der Monat ist gesperrt.
          </span>
        </div>
      )}
      {justSubmitted && (
        <div className="flex items-center gap-2 rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2 text-sm text-green-700 dark:text-green-400">
          <CheckCircle2 size={16} className="shrink-0" />
          <span>Danke — dein Monatsbericht wurde rechtsverbindlich abgegeben.</span>
        </div>
      )}
      {loadError && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {loadError}
        </div>
      )}

      {/* AP-24: Werkzeugleiste der händischen Erfassung */}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={runPrefill}
            disabled={prefilling}
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50"
          >
            <Download size={15} /> {prefilling ? 'Wird übernommen…' : 'Aus Zeiterfassung vorbefüllen'}
          </button>
          <span className="text-xs text-muted-foreground">
            Zeile anklicken, um Stunden, Fehlzeit und Diäten zu erfassen.
          </span>
        </div>
      )}
      {nurAnzeige && (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Die Stunden kommen aus der Zeiterfassung und lassen sich hier nicht ändern.
            Fehlzeiten erfasst du unter <strong className="font-medium text-foreground">Fehlzeiten</strong>
            {hatDiaeten ? (
              <>
                , Diäten beim <strong className="font-medium text-foreground">Stempeln</strong> über die
                Tagesdaten
              </>
            ) : null}
            .
          </span>
        </div>
      )}
      {hint && (
        <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
          {hint}
        </div>
      )}

      {/* Tabelle */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : !report || (!hasRows && !canEdit) ? (
        <div className="rounded-lg border border-border bg-muted/20 py-10 text-center text-sm text-muted-foreground">
          Für {report?.period.label ?? 'diesen Monat'} sind keine Zeiten oder Fehlzeiten erfasst.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                {report.columns.map((c) => (
                  <th
                    key={c.key}
                    className={`whitespace-nowrap px-3 py-2 font-semibold text-muted-foreground ${alignCls(c.align)}`}
                  >
                    {c.headerHidden ? '' : c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, i) => (
                <tr
                  key={row.date}
                  onClick={() => canEdit && setEditDate(row.date)}
                  className={[
                    'group border-b border-border/60 last:border-0',
                    canEdit ? 'cursor-pointer hover:bg-accent/50' : '',
                    // Wochengrenze: kräftigere Linie oberhalb jedes Montags.
                    i > 0 && isWeekStart(row.date) ? 'border-t-2 border-t-border' : '',
                    isWeekend(row.date) ? 'bg-muted/25' : '',
                  ].join(' ')}
                >
                  {report.columns.map((c, i) => (
                    <td
                      key={c.key}
                      className={`whitespace-nowrap px-3 py-1.5 tabular-nums text-foreground ${alignCls(c.align)}`}
                    >
                      {i === 0 && canEdit ? (
                        <span className="inline-flex items-center gap-1.5">
                          {cellValue(c, row)}
                          <Pencil
                            size={12}
                            className="opacity-0 transition-opacity group-hover:opacity-60"
                            aria-hidden="true"
                          />
                        </span>
                      ) : (
                        cellValue(c, row)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-border bg-muted/30 font-semibold">
                {report.columns.map((c, i) => (
                  <td
                    key={c.key}
                    className={`whitespace-nowrap px-3 py-2 tabular-nums text-foreground ${alignCls(c.align)}`}
                  >
                    {i === 0 ? 'Summe' : totalValue(c)}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Aktionen */}
      {report && hasRows && (
        <div className="flex flex-wrap gap-3">
          {locked ? (
            <button
              onClick={viewSubmittedPdf}
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground hover:bg-accent"
            >
              <FileText size={16} /> Abgegebenes PDF
            </button>
          ) : (
            <>
              <button
                onClick={viewPdf}
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground hover:bg-accent"
              >
                <FileText size={16} /> PDF ansehen
              </button>
              <button
                onClick={openSubmit}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              >
                <ShieldCheck size={16} /> Verbindlich abgeben
              </button>
            </>
          )}
          {/* Ersetzt die Abgabe nicht — abgegeben geht das eingefrorene PDF hinaus, sonst der heutige Stand. */}
          <button
            onClick={() => setMailOffen(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground hover:bg-accent"
          >
            <Mail size={16} /> {t('mailExport.menu')}
          </button>
        </div>
      )}

      {report && (
        <MailExportDialog
          open={mailOffen}
          onClose={() => setMailOffen(false)}
          source={locked && report.submission ? 'MONTHLY_REPORT_SUBMISSION' : 'MONTHLY_REPORT'}
          title={t('mailExport.titles.MONTHLY_REPORT')}
          defaultSubject={[
            t('mailExport.titles.MONTHLY_REPORT'),
            [ich?.profile?.firstName, ich?.profile?.lastName].filter(Boolean).join(' '),
            report.period.label,
          ].filter(Boolean).join(' ')}
          request={() =>
            locked && report.submission
              ? monthlyReportApi.submissionPdfRequest(report.submission.id)
              : monthlyReportApi.exportPdfRequest(year, month)
          }
          formats={['pdf']}
          notice={locked && report.submission ? undefined : t('mailExport.notSubmitted')}
        />
      )}

      {/* AP-24: Stunden eines Tages ändern */}
      {editDate && (
        <ReportDayDialog
          date={editDate}
          onClose={() => setEditDate(null)}
          onSaved={() => { setEditDate(null); void load() }}
        />
      )}

      {/* Abgabe-Dialog (FES) */}
      {showSubmit && report && (
        <Modal
          isOpen
          onClose={() => !submitting && setShowSubmit(false)}
          variant="center"
          size="md"
          padded={false}
          hideCloseButton
        >
          <div className="space-y-4 p-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 shrink-0 text-primary" />
              <h2 className="text-base font-semibold text-foreground">
                Monatsbericht {report.period.label} abgeben
              </h2>
            </div>

            <p className="rounded-lg bg-muted/40 p-3 text-sm text-muted-foreground">
              {report.confirmationText}
            </p>

            <label className="flex items-start gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]"
              />
              <span>Ich habe den Bericht geprüft und bestätige die Richtigkeit.</span>
            </label>

            <div className="border-t border-border pt-4">
              {/* Umschalter: Passwort oder PIN */}
              <div className="mx-auto mb-3 flex max-w-xs overflow-hidden rounded-lg border border-border">
                {(['password', 'pin'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => { setAuthMode(m); setSubmitError(null) }}
                    disabled={submitting}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      authMode === m
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {m === 'password' ? 'Passwort' : 'PIN'}
                  </button>
                ))}
              </div>

              {authMode === 'password' ? (
                <div>
                  <label className="mb-1.5 block text-sm text-muted-foreground" htmlFor="mr-pass">
                    Zur Bestätigung deiner Identität bitte Passwort eingeben
                  </label>
                  <input
                    id="mr-pass"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    disabled={submitting || !accepted}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void submit() }}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  />
                </div>
              ) : (
                <div>
                  <label className="mb-1.5 block text-sm text-muted-foreground" htmlFor="mr-pin">
                    Zur Bestätigung deiner Identität bitte PIN eingeben
                  </label>
                  <input
                    id="mr-pin"
                    type="password"
                    inputMode="numeric"
                    autoComplete="off"
                    value={pin}
                    disabled={submitting || !accepted}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
                    onKeyDown={(e) => { if (e.key === 'Enter') void submit() }}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  />
                </div>
              )}
            </div>

            {submitError && (
              <p className="text-center text-sm text-destructive">{submitError}</p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowSubmit(false)}
                disabled={submitting}
                className="flex-1 rounded-xl bg-muted py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted/80 disabled:opacity-40"
              >
                Abbrechen
              </button>
              <button
                onClick={submit}
                disabled={submitting || !accepted || !credentialReady}
                className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
              >
                {submitting ? 'Wird abgegeben…' : 'Verbindlich abgeben'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
